import { useState, useCallback } from "react";
import { Button, Col, Form, Row, Table } from "reactstrap";
import Modal from "../../../../components/Common/Modal";
import { TDeviceDetails } from "../../../../common/types";
import { useAssignDeviceOwner, useRemoveDeviceAsOwner, useAddDeviceAsOperator, useRemoveDeviceAsOperator, useAddDeviceAsDealer, useRemoveDeviceAsDealer } from "../../../../common/hooks/api/device";
import { toastSuccess, toastError } from "../../../../helpers/toast-helper";
import { useQueryClient } from "@tanstack/react-query";
import AsyncSelect from "react-select/async";
import { getResponseData } from "../../../../helpers/response-helper";
import { getUserList } from "../../../../api/user";

interface Props {
    isOpen: boolean;
    onToggle: () => void;
    device: TDeviceDetails | null;
}

type UserRoleType = 'owner' | 'dealer' | 'operator';

const EditDeviceModal = ({ isOpen, onToggle, device }: Props) => {
    const queryClient = useQueryClient();
    const assignOwnerMutation = useAssignDeviceOwner();
    const removeOwnerMutation = useRemoveDeviceAsOwner();
    const addOperatorMutation = useAddDeviceAsOperator();
    const removeOperatorMutation = useRemoveDeviceAsOperator();
    const addDealerMutation = useAddDeviceAsDealer();
    const removeDealerMutation = useRemoveDeviceAsDealer();

    const [selectedUserForRole, setSelectedUserForRole] = useState<{ owner?: { value: number; label: string } | null; dealer?: { value: number; label: string } | null; operator?: { value: number; label: string } | null }>({
        owner: null,
        dealer: null,
        operator: null,
    });

    const loadUserOptions = useCallback(async (inputValue: string) => {
        try {
            const userList = await getUserList({
                pagination: { pageSize: 100, pageIndex: 1 },
                sorting: { sortBy: undefined, isDescending: undefined },
                filtering: { searchTerm: inputValue || "" },
            });
            const { data: users } = getResponseData(userList);
            return (users || []).map((user: any) => ({
                value: user.id,
                label: `${user.firstName} ${user.lastName} (${user.email})`,
            }));
        } catch {
            return [];
        }
    }, []);

    const handleAddUser = async (roleType: UserRoleType) => {
        if (!device || !selectedUserForRole[roleType]) return;

        const userId = selectedUserForRole[roleType]!.value;
        const deviceSerialNumber = device.serialNumber;

        try {
            let mutation;
            switch (roleType) {
                case 'owner':
                    mutation = assignOwnerMutation;
                    break;
                case 'dealer':
                    mutation = addDealerMutation;
                    break;
                case 'operator':
                    mutation = addOperatorMutation;
                    break;
            }

            await mutation.mutateAsync({ userId, deviceSerialNumber });
            toastSuccess(`Added ${roleType} successfully`);
            queryClient.invalidateQueries({ queryKey: ['device', deviceSerialNumber] });
            setSelectedUserForRole({ ...selectedUserForRole, [roleType]: null });
        } catch {
            toastError(`Failed to add ${roleType}`);
        }
    };

    const handleRemoveUser = async (roleType: UserRoleType, userId: number) => {
        if (!device) return;

        const deviceSerialNumber = device.serialNumber;

        try {
            let mutation;
            switch (roleType) {
                case 'owner':
                    mutation = removeOwnerMutation;
                    break;
                case 'dealer':
                    mutation = removeDealerMutation;
                    break;
                case 'operator':
                    mutation = removeOperatorMutation;
                    break;
            }

            await mutation.mutateAsync({ userId, deviceSerialNumber });
            toastSuccess(`Removed ${roleType} successfully`);
            queryClient.invalidateQueries({ queryKey: ['device', deviceSerialNumber] });
        } catch {
            toastError(`Failed to remove ${roleType}`);
        }
    };

    const renderUserList = (users: any[], roleType: UserRoleType) => {
        if (!users || users.length === 0) {
            return <div className="text-muted">No {roleType}s assigned</div>;
        }

        return (
            <Table striped>
                <thead>
                    <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {users.map((user) => (
                        <tr key={user.id}>
                            <td>{user.firstName} {user.lastName}</td>
                            <td>{user.email}</td>
                            <td>
                                <Button
                                    color="danger"
                                    size="sm"
                                    onClick={() => handleRemoveUser(roleType, user.id)}
                                    disabled={
                                        (roleType === 'owner' && removeOwnerMutation.isPending) ||
                                        (roleType === 'dealer' && removeDealerMutation.isPending) ||
                                        (roleType === 'operator' && removeOperatorMutation.isPending)
                                    }
                                >
                                    Remove
                                </Button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        );
    };

    const handleClose = () => {
        setSelectedUserForRole({ owner: null, dealer: null, operator: null });
        onToggle();
    };

    if (!device) return null;

    return (
        <Modal title="Edit Device" isOpen={isOpen} onToggle={handleClose}>
            <Form>
                <Row className="mb-4">
                    <Col xs={12}>
                        <h5 className="mb-3">Device Serial Number: {device.serialNumber}</h5>
                    </Col>
                </Row>

                {/* Owners Section */}
                <Row className="mb-4">
                    <Col xs={12}>
                        <h6 className="mb-2">Owners</h6>
                        <div className="mb-3">
                            <AsyncSelect
                                placeholder="Search for a user to add as owner..."
                                loadOptions={loadUserOptions}
                                value={selectedUserForRole.owner}
                                onChange={(option) => setSelectedUserForRole({ ...selectedUserForRole, owner: option })}
                                isClearable
                            />
                        </div>
                        <Button
                            color="primary"
                            size="sm"
                            onClick={() => handleAddUser('owner')}
                            disabled={!selectedUserForRole.owner || assignOwnerMutation.isPending}
                            className="mb-3"
                        >
                            Add Owner
                        </Button>
                        {renderUserList(device.owners || [], 'owner')}
                    </Col>
                </Row>

                {/* Dealers Section */}
                <Row className="mb-4">
                    <Col xs={12}>
                        <h6 className="mb-2">Dealers</h6>
                        <div className="mb-3">
                            <AsyncSelect
                                placeholder="Search for a user to add as dealer..."
                                loadOptions={loadUserOptions}
                                value={selectedUserForRole.dealer}
                                onChange={(option) => setSelectedUserForRole({ ...selectedUserForRole, dealer: option })}
                                isClearable
                            />
                        </div>
                        <Button
                            color="primary"
                            size="sm"
                            onClick={() => handleAddUser('dealer')}
                            disabled={!selectedUserForRole.dealer || addDealerMutation.isPending}
                            className="mb-3"
                        >
                            Add Dealer
                        </Button>
                        {renderUserList(device.dealers || [], 'dealer')}
                    </Col>
                </Row>

                {/* Operators Section */}
                <Row className="mb-4">
                    <Col xs={12}>
                        <h6 className="mb-2">Operators</h6>
                        <div className="mb-3">
                            <AsyncSelect
                                placeholder="Search for a user to add as operator..."
                                loadOptions={loadUserOptions}
                                value={selectedUserForRole.operator}
                                onChange={(option) => setSelectedUserForRole({ ...selectedUserForRole, operator: option })}
                                isClearable
                            />
                        </div>
                        <Button
                            color="primary"
                            size="sm"
                            onClick={() => handleAddUser('operator')}
                            disabled={!selectedUserForRole.operator || addOperatorMutation.isPending}
                            className="mb-3"
                        >
                            Add Operator
                        </Button>
                        {renderUserList(device.operators || [], 'operator')}
                    </Col>
                </Row>

                <Row>
                    <Col className="text-end">
                        <Button
                            type="button"
                            color="secondary"
                            onClick={handleClose}
                        >
                            Close
                        </Button>
                    </Col>
                </Row>
            </Form>
        </Modal>
    );
};

export default EditDeviceModal;

